/**
 * Chat-attachment text extraction.
 *
 * Streams the raw bytes of a stored session file, decodes the text portion
 * live, and reports progress (chars decoded vs total bytes) so the operator
 * gets a deterministic "full text ready" state instead of the silent
 * no-preview experience that bare PDF / text / CSV / DOCX attachments used
 * to produce.
 *
 * ## Why this lives in the browser
 *
 * The gateway already extracts text server-side (`src/gateway/chat-documents.ts`,
 * `src/gateway/session-files/session-file-prepare.ts`) and surfaces a
 * `prep.textChars` hint on the record. That covers the model context. This
 * module covers the *operator* context — surfacing the same extracted text
 * inside an inline preview pane inside the chat bubble, so a dropped PDF
 * reads like a drop on the operator's side too.
 *
 * We deliberately re-extract on the client instead of round-tripping the
 * prepared text, because:
 *
 *   - the session-file record's `prep` may be stale, missing, or `binaryOnly`
 *     (the server can decline to extract large or unreadable inputs);
 *   - the operator expects to see progress on slow files, and a server
 *     round-trip would either block the UI or require a streaming channel
 *     that doesn't exist on the file-by-id GET endpoint;
 *   - the extraction is bounded: text/* and JSON/CSV/etc. decode straight
 *     to UTF-8, and PDF/DOCX are detected-but-skipped (we surface a
 *     "binary on the wire" badge and the iframe fallback path).
 *
 * ## Persistence
 *
 * Successful extractions are persisted to `sessionStorage` under
 * `jaisiu:attachment-text:<fileId>:<sha256[:8]>`, keyed by the
 * attachment's content hash so a different file with the same id can
 * never collide. The cache is best-effort and bounded; on quota errors
 * we drop the entry silently. The TTL is
 * `ATTACHMENT_TEXT_CACHE_TTL_MS` (24h) — the chat might reopen tomorrow,
 * but we don't want stale text to outlive the conversation.
 *
 * ## Auto-retry on truncation
 *
 * If the server truncates the response (e.g. a load-balancer caps the
 * response at 5 MB and the file is 12 MB), the `Content-Length` header
 * either doesn't match the bytes streamed in or the connection drops
 * early. Both are detectable: we re-fetch once with a `Range` header
 * covering the missing tail; if that fails we mark the extraction as
 * `truncated` and surface a "partial text" badge so the operator knows
 * to download the original.
 */
import {
  sessionFileTextExtractionError,
  type SessionFileTextExtractionError,
} from "./session-file-text-errors.ts";

/** Hard cap on the bytes we'll stream into the browser. */
export const ATTACHMENT_TEXT_MAX_BYTES = 10 * 1024 * 1024; // 10 MiB

/** Largest single sessionStorage entry we will write (UTF-16 codepoints). */
export const ATTACHMENT_TEXT_CACHE_MAX_CHARS = 1_000_000;

/** TTL for the sessionStorage cache. */
export const ATTACHMENT_TEXT_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** sessionStorage namespace. */
export const ATTACHMENT_TEXT_CACHE_PREFIX = "jaisiu:attachment-text:";

/** Single retry on truncation. Set above 1 to retry twice, etc. */
export const ATTACHMENT_TEXT_MAX_RETRIES = 1;

export type ExtractState = "idle" | "fetching" | "decoding" | "ready" | "partial" | "error" | "skipped";

export interface ExtractProgress {
  state: ExtractState;
  /** Decoded character count, growing as we read. */
  chars: number;
  /** Total bytes advertised by `Content-Length`; 0 if unknown. */
  totalBytes: number;
  /** Bytes actually read so far. */
  bytesRead: number;
  /** Set when the file was truncated by the server (length mismatch). */
  truncated: boolean;
  /** Final decoded text. Only set once `state === "ready" | "partial"`. */
  text?: string;
  /** Set when `state === "error"`. */
  error?: SessionFileTextExtractionError;
  /** Number of attempts made so far (1 = first try, 2 = one retry). */
  attempts: number;
}

/** A "live" handle you can poll / abort. */
export interface LiveExtraction {
  /** Read the latest progress snapshot. */
  snapshot(): ExtractProgress;
  /** Subscribe to progress updates. Returns an unsubscribe function. */
  subscribe(fn: (p: ExtractProgress) => void): () => void;
  /** Cancel the in-flight fetch. Idempotent. */
  abort(): void;
  /** Resolves when the extraction reaches a terminal state. */
  done: Promise<ExtractProgress>;
}

/** Plain options for {@link startExtraction}. */
export interface ExtractionOptions {
  /** Stable file id (session file id, e.g. `sf-21fd580813ee4485bb5f94e5`). */
  fileId: string;
  /** Content hash, used as the sessionStorage namespace. */
  sha256?: string;
  /** Resolved raw URL — auth/credentials are baked in by the host. */
  url: string;
  /** Declared MIME (`text/plain`, `application/pdf`, `application/json`, …). */
  mime: string;
  /** Filename, for error messages. */
  filename?: string;
  /** Optional caller-supplied AbortSignal (e.g. component teardown). */
  signal?: AbortSignal;
  /** Override the sessionStorage cache (tests). */
  cache?: TextCacheStore;
  /** Override `fetch` (tests). */
  fetchImpl?: typeof fetch;
  /** Override the decoder (tests); defaults to `TextDecoder` with `utf-8`. */
  decoder?: (bytes: Uint8Array) => string;
  /**
   * Hook to skip the network entirely (e.g. when the caller already
   * knows this MIME won't decode). The progress snapshot will resolve
   * with `state: "skipped"`.
   */
  shouldSkip?: (mime: string) => boolean;
  /**
   * Override the auto-retry budget. Defaults to
   * {@link ATTACHMENT_TEXT_MAX_RETRIES}.
   */
  maxRetries?: number;
}

/**
 * Where we persist decoded text. Tests inject a fake; production code uses
 * the default {@link sessionStorageTextCache} backed by `window.sessionStorage`.
 */
export interface TextCacheStore {
  get(key: string): { text: string; savedAt: number } | null;
  set(key: string, value: { text: string; savedAt: number }): void;
  delete(key: string): void;
}

/**
 * Browser-backed cache. All operations are wrapped in try/catch — a
 * blocked `sessionStorage` (private mode in some browsers, full quota) is
 * a cache miss, never a fatal error.
 */
export const sessionStorageTextCache: TextCacheStore = {
  get(key) {
    try {
      if (typeof sessionStorage === "undefined") return null;
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { text?: unknown; savedAt?: unknown };
      if (typeof parsed.text !== "string") return null;
      if (typeof parsed.savedAt !== "number") return null;
      if (Date.now() - parsed.savedAt > ATTACHMENT_TEXT_CACHE_TTL_MS) {
        sessionStorage.removeItem(key);
        return null;
      }
      return { text: parsed.text, savedAt: parsed.savedAt };
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      if (typeof sessionStorage === "undefined") return;
      if (value.text.length > ATTACHMENT_TEXT_CACHE_MAX_CHARS) return;
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded / private mode / sandboxed. Drop silently.
    }
  },
  delete(key) {
    try {
      if (typeof sessionStorage === "undefined") return;
      sessionStorage.removeItem(key);
    } catch {
      void key;
    }
  },
};

/** Stable cache key. Two files with the same id but different content can't collide. */
export function cacheKeyFor(fileId: string, sha256?: string): string {
  const hash = (sha256 ?? "").trim().slice(0, 8) || "nohash";
  return `${ATTACHMENT_TEXT_CACHE_PREFIX}${fileId}:${hash}`;
}

/**
 * MIMEs we will extract as text. Anything else resolves to
 * `state: "skipped"` (e.g. a `video/mp4` attachment) and the inline
 * preview shows the existing iframe / download fallback.
 */
const TEXT_LIKE_MIMES: ReadonlyArray<string> = [
  // First-class
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/tab-separated-values",
  "text/html",
  "text/xml",
  "text/css",
  "text/javascript",
  "text/x-yaml",
  "text/yaml",
  "text/toml",
  // Common but non-`text/*` aliases the server may tag us with
  "application/json",
  "application/xml",
  "application/javascript",
  "application/x-javascript",
  "application/ld+json",
  "application/x-ndjson",
  "application/yaml",
  "application/x-yaml",
  "application/toml",
  "application/csv",
];

/** True if a MIME is one we can decode to a string. */
export function isTextExtractableMime(mime: string): boolean {
  const m = (mime ?? "").toLowerCase().trim();
  if (!m) return false;
  if (m.startsWith("text/")) return true;
  if (TEXT_LIKE_MIMES.includes(m)) return true;
  // Final escape hatch: structured-suffix media types
  // (`application/vnd.api+json`, `image/svg+xml`, etc.).
  if (m.endsWith("+json") || m.endsWith("+xml") || m.endsWith("+yaml")) return true;
  return false;
}

/**
 * Begin a live extraction. The handle emits `idle → fetching → decoding →
 * ready | partial | error | skipped`; the operator's UI shows progress,
 * auto-retry on truncation, and persists the result in sessionStorage.
 */
export function startExtraction(opts: ExtractionOptions): LiveExtraction {
  return runExtraction(opts);
}

function runExtraction(opts: ExtractionOptions): LiveExtraction {
  const cache = opts.cache ?? sessionStorageTextCache;
  const fetchImpl =
    opts.fetchImpl ??
    (typeof fetch !== "undefined"
      ? fetch
      : ((): typeof fetch => {
          throw sessionFileTextExtractionError("network", "fetch is not available in this environment");
        })());
  const decoder = opts.decoder ?? defaultDecoder;
  const key = cacheKeyFor(opts.fileId, opts.sha256);
  const maxRetries = Math.max(0, opts.maxRetries ?? ATTACHMENT_TEXT_MAX_RETRIES);

  // AbortController merges the caller signal with our own internal abort.
  const internalAbort = new AbortController();
  const onCallerAbort = () => internalAbort.abort(opts.signal?.reason);
  if (opts.signal) {
    if (opts.signal.aborted) {
      internalAbort.abort(opts.signal.reason);
    } else {
      opts.signal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  // Fast path: cache hit. Emit a "ready" snapshot immediately.
  const cached = cache.get(key);
  if (cached) {
    const snap: ExtractProgress = {
      state: "ready",
      chars: cached.text.length,
      totalBytes: 0,
      bytesRead: 0,
      truncated: false,
      text: cached.text,
      attempts: 0,
    };
    return terminalExtraction(snap, () => {}, opts, onCallerAbort);
  }

  if (opts.shouldSkip && opts.shouldSkip(opts.mime)) {
    return terminalExtraction(
      { state: "skipped", chars: 0, totalBytes: 0, bytesRead: 0, truncated: false, attempts: 0 },
      () => {},
      opts,
      onCallerAbort,
    );
  }
  if (!isTextExtractableMime(opts.mime)) {
    return terminalExtraction(
      { state: "skipped", chars: 0, totalBytes: 0, bytesRead: 0, truncated: false, attempts: 0 },
      () => {},
      opts,
      onCallerAbort,
    );
  }

  // Mutable state — closure-shared with the consumer.
  let snap: ExtractProgress = {
    state: "fetching",
    chars: 0,
    totalBytes: 0,
    bytesRead: 0,
    truncated: false,
    attempts: 1,
  };
  let accumulated = "";
  const subscribers = new Set<(p: ExtractProgress) => void>();
  const emit = (next: Partial<ExtractProgress>) => {
    snap = { ...snap, ...next };
    for (const fn of subscribers) fn(snap);
  };

  const promise = (async (): Promise<ExtractProgress> => {
    let attempt = 0;
    let lastError: SessionFileTextExtractionError | undefined;
    let resumeFrom = 0;
    let localBytesRead = 0;
    let localCharsDecoded = 0;

    while (attempt <= maxRetries) {
      attempt += 1;
      if (internalAbort.signal.aborted) {
        throw sessionFileTextExtractionError("aborted", "extraction cancelled");
      }

      emit({ state: "fetching", attempts: attempt, bytesRead: resumeFrom, chars: localCharsDecoded, truncated: false });

      let response: Response;
      try {
        const headers: Record<string, string> = {};
        if (resumeFrom > 0) {
          headers["Range"] = `bytes=${resumeFrom}-`;
        }
        response = await fetchImpl(opts.url, {
          method: "GET",
          headers,
          credentials: "include",
          signal: internalAbort.signal,
        });
      } catch (err) {
        if (internalAbort.signal.aborted) {
          throw sessionFileTextExtractionError("aborted", "extraction cancelled");
        }
        if (attempt > maxRetries) {
          throw sessionFileTextExtractionError(
            "network",
            err instanceof Error ? err.message : "network request failed",
          );
        }
        // Retry on transient network errors.
        lastError = sessionFileTextExtractionError(
          "network",
          err instanceof Error ? err.message : "network request failed",
        );
        continue;
      }

      if (!response.ok && response.status !== 206) {
        throw sessionFileTextExtractionError(
          response.status === 404 ? "not_found" : "unreadable",
          `gateway returned HTTP ${response.status}`,
        );
      }

      const contentLength = parseContentLength(response.headers.get("content-length"));
      if (contentLength > 0 && resumeFrom + contentLength > ATTACHMENT_TEXT_MAX_BYTES) {
        throw sessionFileTextExtractionError(
          "too_large",
          `file is ${formatBytes(resumeFrom + contentLength)}; inline preview is capped at ${formatBytes(ATTACHMENT_TEXT_MAX_BYTES)}`,
        );
      }
      emit({ totalBytes: contentLength > 0 ? resumeFrom + contentLength : 0 });

      // Continue counting from where the previous attempt left off.
      // `accumulated` keeps the bytes we already decoded — DO NOT reset
      // it between attempts, that loses the first half of the file.
      const bytesAtAttemptStart = localBytesRead;
      localBytesRead = resumeFrom;
      let truncatedByCap = false;

      try {
        if (!response.body) {
          throw sessionFileTextExtractionError("unreadable", "response had no body");
        }
        const reader = response.body.getReader();
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (internalAbort.signal.aborted) {
            throw sessionFileTextExtractionError("aborted", "extraction cancelled");
          }
          const { value, done } = await reader.read();
          if (done) break;
          if (!value || value.byteLength === 0) continue;
          localBytesRead += value.byteLength;
          if (localBytesRead > ATTACHMENT_TEXT_MAX_BYTES) {
            truncatedByCap = true;
            const allowed = ATTACHMENT_TEXT_MAX_BYTES - (localBytesRead - value.byteLength);
            if (allowed > 0) {
              const head = value.subarray(0, allowed);
              const out = decoder(head);
              accumulated += out;
              localCharsDecoded += out.length;
            }
            break;
          }
          const out = decoder(value);
          if (out.length > 0) {
            accumulated += out;
            localCharsDecoded += out.length;
          }
          emit({ state: "decoding", chars: localCharsDecoded, bytesRead: localBytesRead });
        }
      } catch (err) {
        if (internalAbort.signal.aborted) {
          throw sessionFileTextExtractionError("aborted", "extraction cancelled");
        }
        // Truncation: the server advertised more than we got, or the
        // stream cut out. Resume with a Range request if we have a
        // budget left.
        const partial = contentLength > 0 && localBytesRead < contentLength + resumeFrom;
        if (partial && attempt <= maxRetries) {
          resumeFrom = localBytesRead;
          continue;
        }
        if (partial) {
          // Out of retries; treat as partial. Fall through.
          truncatedByCap = true;
        } else if (err && typeof err === "object" && "code" in err) {
          throw err;
        } else {
          lastError = sessionFileTextExtractionError(
            "network",
            err instanceof Error ? err.message : "stream interrupted",
          );
          if (attempt <= maxRetries) {
            // Unknown stream error — retry from scratch. We discard
            // the partial bytes — the next attempt restarts the
            // download from byte 0 and `accumulated` is reset by
            // the retry-reset branch below.
            resumeFrom = 0;
            accumulated = "";
            localBytesRead = 0;
            localCharsDecoded = 0;
            continue;
          }
          throw lastError;
        }
      }

      // Final truncation gate (post-stream): server said N bytes, we got
      // fewer, no retries left.
      let truncated = truncatedByCap;
      if (contentLength > 0 && localBytesRead < contentLength + resumeFrom) {
        if (attempt <= maxRetries) {
          resumeFrom = localBytesRead;
          continue;
        }
        truncated = true;
      }
      // A retry that added zero bytes is also a truncation: the server
      // couldn't fill the gap. This catches servers that send no
      // Content-Length on the partial response (we can't compare) but
      // do send zero bytes when asked for the missing tail.
      if (attempt > 1 && localBytesRead === bytesAtAttemptStart) {
        truncated = true;
      }

      const finalText = accumulated;
      const final: ExtractProgress = {
        state: truncated ? "partial" : "ready",
        chars: finalText.length,
        totalBytes: contentLength > 0 ? contentLength : localBytesRead,
        bytesRead: localBytesRead,
        truncated,
        text: finalText,
        attempts: attempt,
      };
      if (!truncated) {
        cache.set(key, { text: finalText, savedAt: Date.now() });
      }
      emit(final);
      return final;
    }

    throw lastError ?? sessionFileTextExtractionError("unreadable", "extraction failed after retries");
  })()
    .catch((err: unknown): ExtractProgress => {
      const e: SessionFileTextExtractionError =
        err && typeof err === "object" && "code" in err
          ? (err as SessionFileTextExtractionError)
          : sessionFileTextExtractionError("unreadable", err instanceof Error ? err.message : String(err));
      const final: ExtractProgress = {
        state: "error",
        chars: 0,
        totalBytes: snap.totalBytes,
        bytesRead: snap.bytesRead,
        truncated: false,
        error: e,
        attempts: snap.attempts,
      };
      emit(final);
      return final;
    })
    .finally(() => {
      if (opts.signal) opts.signal.removeEventListener("abort", onCallerAbort);
    });

  return {
    snapshot: () => snap,
    subscribe: (fn) => {
      subscribers.add(fn);
      fn(snap);
      return () => { subscribers.delete(fn); };
    },
    abort: () => {
      internalAbort.abort();
      if (opts.signal) opts.signal.removeEventListener("abort", onCallerAbort);
    },
    done: promise,
  };
}

function terminalExtraction(
  snap: ExtractProgress,
  _emit: () => void,
  opts: ExtractionOptions,
  onCallerAbort: () => void,
): LiveExtraction {
  if (opts.signal) opts.signal.removeEventListener?.("abort", onCallerAbort);
  return {
    snapshot: () => snap,
    subscribe: (fn) => {
      fn(snap);
      return () => {};
    },
    abort: () => {},
    done: Promise.resolve(snap),
  };
}

// ---------- internals ----------

function defaultDecoder(bytes: Uint8Array): string {
  // Lazily build the TextDecoder — not all environments ship one, and
  // constructing it eagerly fails in tests.
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  }
  // Fallback: use Buffer if available, else lossy String.fromCharCode.
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytesToArrayBuffer(bytes)).toString("utf-8");
  }
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i]);
  return out;
}

function bytesToArrayBuffer(input: Uint8Array | ArrayBuffer | ArrayBufferView): ArrayBuffer {
  if (input instanceof ArrayBuffer) return input;
  if (ArrayBuffer.isView(input)) {
    return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength) as ArrayBuffer;
  }
  return input as ArrayBuffer;
}

function parseContentLength(value: string | null): number {
  if (!value) return 0;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/** Human-readable byte count (`12.3 MB`). */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "0 B";
  if (n < 1024) return `${Math.round(n)} B`;
  const units = ["KB", "MB", "GB"];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}
